import { MenuItem, Stack, TextField } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { RECEIVER_ROLE_OPTIONS } from "../../consts";
import { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import ClientAutocomplete from "./clientAutocomplete.component";

interface ReceiverFieldsProps {
  receiver: OptionRecord | null;
  onReceiverChange: (value: OptionRecord | null) => void;
  role: string;
  onRoleChange: (value: string) => void;
  receiverError?: string | null;
  roleError?: string | null;
  /** Rendered at the end of the row, e.g. a remove button. */
  action?: ReactNode;
}

/**
 * The receiver (odbiorca) picker and its FA(3) role, shared by the invoice
 * form and a client's invoice defaults so both ask for it the same way.
 *
 * The role has no default: it sets the buyer's JST/GV markers in KSeF, so it
 * has to be a deliberate choice - it becomes required once a receiver is set.
 */
export default function ReceiverFields({
  receiver,
  onReceiverChange,
  role,
  onRoleChange,
  receiverError,
  roleError,
  action,
}: ReceiverFieldsProps) {
  const { t } = useTranslation();

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems="flex-start"
    >
      <ClientAutocomplete
        value={receiver}
        onChange={onReceiverChange}
        label={t("Receiver")}
        error={receiverError}
        sx={{ flex: 1, width: "100%" }}
      />
      <TextField
        size="small"
        select
        fullWidth
        label={t("Receiver role")}
        value={role}
        onChange={(event) => onRoleChange(event.target.value)}
        required={!!receiver}
        error={!!roleError}
        helperText={
          roleError ?? t("Subordinate units and VAT group members need a NIP.")
        }
        sx={{ flex: 1 }}
      >
        {RECEIVER_ROLE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {t(option.labelKey)}
          </MenuItem>
        ))}
      </TextField>
      {action}
    </Stack>
  );
}
